import { FC, useRef, useState } from 'react'
import { Button, Form, Modal, Space, Upload, message, notification } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import Task from '../../../types/api/task'
import { useForm } from 'antd/es/form/Form'
import { UploadFile as UploadFileInterface } from 'antd/es/upload'
import UploadFile from '../../../components/form-items/upload-file/upload-file'
import PreviewShipmentBlank from '../../../components/buttons/preview-shipment-blank/preview-shipment-blank'
import { BLANK_TYPE_BUTTON_COLOR } from '../../../types/api/shipment-blank-type'
import useUploadFiles from '../../../hooks/api/use-upload-file'
import { tasksApi, useUpdateTaskMutation } from '../../../store/api/tasks-api'
import { useUpdateShipmentMutation } from '../../../store/api/shipments-api'
import ColorList from './color-list/color-list'
import Color from '../../../types/api/components/shipment/color'
import useErrorModal from '../../../hooks/api/use-error-modal'

interface CompleteDrawingBlankProps {
    task: Task
}

const CompleteDrawingBlank: FC<CompleteDrawingBlankProps> = ({ task }) => {
    const dispatch = useDispatch()
    const [open, setOpen] = useState(false)
    const [form] = useForm()
    const [updateShipment, updateShipmentState] = useUpdateShipmentMutation()
    const [uploadFiles, uploadState] = useUploadFiles()
    const [updateTask, updateTaskState] = useUpdateTaskMutation()
    const isSavingRef = useRef(false)

    useErrorModal(updateShipmentState, 'Не удалось обновить заказ')
    useErrorModal(updateTaskState, 'Не удалось обновить задачу')

    const shipment = task.shipment
    const isSaving = uploadState.isLoading || updateShipmentState.isLoading
    const hasPreview = Boolean(shipment?.blankPreviewFiles?.length)

    const savePreviewFiles = async (fileList: UploadFileInterface[]) => {
        const shipmentDocId = shipment?.documentId
        if (!shipmentDocId || isSavingRef.current) return

        isSavingRef.current = true
        try {
            const localFiles = fileList.filter(file => file.originFileObj)

            if (!localFiles.length) {
                await updateShipment({
                    docId: shipmentDocId,
                    body: {
                        data: {
                            blankPreviewFiles: { set: [] },
                        },
                    },
                }).unwrap()
                dispatch(tasksApi.util.invalidateTags(['Tasks']))
                return
            }

            const result = await uploadFiles(localFiles)
            if (!result.data?.length) {
                notification.error({ message: 'Не удалось сохранить файл' })
                return
            }

            await updateShipment({
                docId: shipmentDocId,
                body: {
                    data: {
                        //@ts-ignore Strapi media multiple: массив numeric id
                        blankPreviewFiles: result.data.map(file => file.id),
                    },
                },
            }).unwrap()

            dispatch(tasksApi.util.invalidateTags(['Tasks']))
        } finally {
            isSavingRef.current = false
        }
    }

    const onClick = () => {
        setOpen(true)
    }

    const blankPreviewFiles = shipment?.blankPreviewFiles

    const onOk = async () => {
        try {
            await form.validateFields(['colors'])

            const shipmentDocId = shipment?.documentId
            if (!shipmentDocId) {
                notification.error({ message: 'У задачи нет связанной отгрузки' })
                return
            }

            const localFiles = (
                form.getFieldValue('files') as UploadFileInterface[] | undefined
            )?.filter(file => file.originFileObj)
            if (localFiles?.length) {
                await savePreviewFiles(localFiles)
            }

            if (!shipment?.blankPreviewFiles?.length && !localFiles?.length) {
                notification.error({ message: 'Сначала загрузите PDF' })
                return
            }

            const colors: Omit<Color, 'id'>[] = form.getFieldValue('colors')
            await updateShipment({
                docId: shipmentDocId,
                body: { data: { colors } },
            }).unwrap()

            await updateTask({
                docId: task.documentId,
                body: { data: { taskStatus: 'Завершена' } },
            }).unwrap()

            notification.success({ message: 'Задача закрыта' })
            setOpen(false)
        } catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e) return
            console.error(e)
            notification.error({ message: 'Не удалось завершить задачу' })
        }
    }

    return (
        <>
            <Button type='primary' onClick={onClick} disabled={task.taskStatus === 'Завершена'}>
                Завершить
            </Button>

            <Modal
                title='Задача отрисовки бланка'
                open={open}
                okText='Завершить задачу'
                onOk={onOk}
                okButtonProps={{ loading: isSaving || updateTaskState.isLoading }}
                onCancel={() => setOpen(false)}
                cancelButtonProps={{ danger: true }}
            >
                <Form
                    form={form}
                    layout='vertical'
                    onValuesChange={changedValues => {
                        if (!('files' in changedValues)) return
                        void savePreviewFiles(changedValues.files ?? [])
                    }}
                >
                    <ColorList />
                    <UploadFile
                        name='files'
                        label='Файл бланка'
                        extra='Можно загрузить несколько PDF — каждый станет страницей бланка. После загрузки проверьте бланки здесь или в таблице.'
                        multiple
                        accept='.pdf,application/pdf'
                        disabled={isSaving}
                        beforeUpload={file => {
                            const isPdf =
                                file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
                            if (!isPdf) {
                                message.error('Допускаются только PDF-файлы')
                                return Upload.LIST_IGNORE
                            }
                            return false
                        }}
                        rules={[{ required: true, message: 'Загрузите хотя бы один PDF.' }]}
                    />
                    {hasPreview && shipment?.documentId && (
                        <Space size='small'>
                            <PreviewShipmentBlank
                                shipment={shipment}
                                blankType='client'
                                blankPreviewFiles={blankPreviewFiles}
                            >
                                <Button
                                    size='small'
                                    icon={<EyeOutlined />}
                                    color={BLANK_TYPE_BUTTON_COLOR.client}
                                    variant='solid'
                                    disabled={isSaving}
                                >
                                    Клиентский бланк
                                </Button>
                            </PreviewShipmentBlank>
                            <PreviewShipmentBlank
                                shipment={shipment}
                                blankType='production'
                                blankPreviewFiles={blankPreviewFiles}
                            >
                                <Button
                                    size='small'
                                    icon={<EyeOutlined />}
                                    color={BLANK_TYPE_BUTTON_COLOR.production}
                                    variant='solid'
                                    disabled={isSaving}
                                >
                                    Произв. бланк
                                </Button>
                            </PreviewShipmentBlank>
                        </Space>
                    )}
                </Form>
            </Modal>
        </>
    )
}

export default CompleteDrawingBlank
